import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Radio, Wifi, WifiOff, Youtube, Facebook, Twitch, 
  ArrowLeft, Copy, ExternalLink, Check, Loader2, Crown,
  Lock, Zap, Monitor, Settings, RefreshCw, Unlink
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const LiveStudio = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [streamKey, setStreamKey] = useState(null);
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    // Check for OAuth callback
    const code = searchParams.get('code');
    if (code) {
      handleOAuthCallback(code);
    }
    
    // Check for subscription success
    if (searchParams.get('subscribed') === 'true') {
      toast.success('Live Studio subscription activated!');
      fetchStatus();
    }
  }, [searchParams]);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/live-studio/status');
      setStatus(response.data);
      
      if (response.data.is_connected) {
        fetchChannels();
        fetchStreamKey();
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await api.get('/live-studio/channels');
      setChannels(response.data.channels || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const fetchStreamKey = async () => {
    try {
      const response = await api.get('/live-studio/stream-key');
      setStreamKey(response.data);
    } catch (error) {
      console.error('Error fetching stream key:', error);
    }
  };

  const handleOAuthCallback = async (code) => {
    setConnecting(true);
    try {
      const redirectUri = `${window.location.origin}/live-studio/callback`;
      const response = await api.post('/live-studio/connect', {
        code,
        redirect_uri: redirectUri
      });
      
      if (response.data.success) {
        toast.success(`Connected as ${response.data.username || 'Restream User'}`);
        fetchStatus();
      }
    } catch (error) {
      toast.error('Failed to connect Restream');
    } finally {
      setConnecting(false);
      // Clean URL
      window.history.replaceState({}, '', '/live-studio');
    }
  };

  const connectRestream = async () => {
    setConnecting(true);
    try {
      const response = await api.get('/live-studio/oauth-url');
      window.location.href = response.data.oauth_url;
    } catch (error) {
      toast.error('Failed to initiate Restream connection');
      setConnecting(false);
    }
  };

  const disconnectRestream = async () => {
    try {
      await api.delete('/live-studio/disconnect');
      toast.success('Restream disconnected');
      setStatus(prev => ({ ...prev, is_connected: false, restream: { connected: false } }));
      setChannels([]);
      setStreamKey(null);
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const response = await api.post('/live-studio/subscribe');
      if (response.data.is_admin) {
        toast.success('Admin access granted - Live Studio is free for you!');
        fetchStatus();
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start subscription');
    } finally {
      setSubscribing(false);
    }
  };

  const getPlatformIcon = (platformName) => {
    const name = platformName?.toLowerCase() || '';
    if (name.includes('youtube')) return <Youtube className="h-5 w-5 text-red-500" />;
    if (name.includes('facebook')) return <Facebook className="h-5 w-5 text-blue-500" />;
    if (name.includes('twitch')) return <Twitch className="h-5 w-5 text-purple-500" />;
    return <Monitor className="h-5 w-5 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Paywall - User doesn't have access
  if (!status?.has_access) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
        <header className="bg-black/30 border-b border-purple-500/20 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button variant="ghost" className="text-purple-300" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Radio className="h-5 w-5 text-red-500" />
              Live Studio
            </h1>
            <div className="w-20" />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="bg-black/40 border-purple-500/30 backdrop-blur-sm overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 via-purple-500 to-blue-500 p-1">
              <div className="bg-gray-900 p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-500 to-purple-500 rounded-full mb-4">
                    <Radio className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Unlock Live Studio</h2>
                  <p className="text-purple-200">Stream to multiple platforms simultaneously</p>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="bg-purple-500/10 rounded-lg p-4 text-center">
                    <Youtube className="h-8 w-8 text-red-500 mx-auto mb-2" />
                    <span className="text-xs text-purple-200">YouTube</span>
                  </div>
                  <div className="bg-purple-500/10 rounded-lg p-4 text-center">
                    <Twitch className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                    <span className="text-xs text-purple-200">Twitch</span>
                  </div>
                  <div className="bg-purple-500/10 rounded-lg p-4 text-center">
                    <Facebook className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                    <span className="text-xs text-purple-200">Facebook</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {[
                    'Stream to 2+ platforms at once',
                    'No separate Restream subscription needed',
                    'Easy OBS/Streamlabs setup',
                    'Real-time chat aggregation',
                    'Stream while creating AI art'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-purple-200">
                      <Check className="h-4 w-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold text-2xl">$7<span className="text-sm font-normal text-purple-300">/month</span></p>
                      <p className="text-purple-300 text-sm">Cancel anytime</p>
                    </div>
                    <Crown className="h-8 w-8 text-amber-400" />
                  </div>
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-red-500 to-purple-500 hover:from-red-600 hover:to-purple-600 text-white py-6 text-lg"
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  data-testid="subscribe-live-studio-btn"
                >
                  {subscribing ? (
                    <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Processing...</>
                  ) : (
                    <><Zap className="h-5 w-5 mr-2" /> Unlock Live Studio</>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  // User has access - Show Live Studio Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
      <header className="bg-black/30 border-b border-purple-500/20 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" className="text-purple-300" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Radio className="h-5 w-5 text-red-500 animate-pulse" />
            Live Studio
            {status?.is_admin && <Badge className="bg-amber-500/20 text-amber-300 ml-2">Admin</Badge>}
          </h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Connection Status */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                {status?.is_connected ? (
                  <Wifi className="h-5 w-5 text-green-400" />
                ) : (
                  <WifiOff className="h-5 w-5 text-red-400" />
                )}
                Restream Connection
              </span>
              {status?.is_connected && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={disconnectRestream}
                >
                  <Unlink className="h-4 w-4 mr-1" />
                  Disconnect
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {status?.is_connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-400">
                  <Check className="h-5 w-5" />
                  <span>Connected as <strong>{status?.restream?.username || 'Restream User'}</strong></span>
                </div>
                
                {/* Stream Key Section */}
                {streamKey && (
                  <div className="bg-purple-500/10 rounded-lg p-4 space-y-3">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      OBS/Streamlabs Settings
                    </h3>
                    
                    <div>
                      <label className="text-purple-300 text-sm">Server URL</label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          value={streamKey.rtmpUrl || 'rtmp://live.restream.io/live'} 
                          readOnly 
                          className="bg-black/30 border-purple-500/30 text-white"
                        />
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="border-purple-500/30"
                          onClick={() => copyToClipboard(streamKey.rtmpUrl || 'rtmp://live.restream.io/live', 'Server URL')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-purple-300 text-sm">Stream Key</label>
                      <div className="flex gap-2 mt-1">
                        <Input 
                          type={showStreamKey ? 'text' : 'password'}
                          value={streamKey.streamKey || '••••••••••••'} 
                          readOnly 
                          className="bg-black/30 border-purple-500/30 text-white"
                        />
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="border-purple-500/30"
                          onClick={() => setShowStreamKey(!showStreamKey)}
                        >
                          {showStreamKey ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="border-purple-500/30"
                          onClick={() => copyToClipboard(streamKey.streamKey, 'Stream Key')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-purple-300/60 text-xs">
                      Use these settings in OBS Studio, Streamlabs, or any RTMP-compatible software
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <WifiOff className="h-12 w-12 text-purple-400/50 mx-auto mb-4" />
                <p className="text-purple-200 mb-4">Connect your Restream account to start multistreaming</p>
                <Button 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  onClick={connectRestream}
                  disabled={connecting}
                  data-testid="connect-restream-btn"
                >
                  {connecting ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Connecting...</>
                  ) : (
                    <><ExternalLink className="h-4 w-4 mr-2" /> Connect Restream</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connected Channels */}
        {status?.is_connected && (
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Monitor className="h-5 w-5 text-purple-400" />
                  Streaming Channels
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-purple-400"
                  onClick={fetchChannels}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {channels.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {channels.map((channel, index) => (
                    <div 
                      key={index}
                      className="bg-purple-500/10 rounded-lg p-3 flex items-center gap-3"
                    >
                      {getPlatformIcon(channel.name || channel.platform)}
                      <div className="flex-1">
                        <p className="text-white font-medium">{channel.name || channel.displayName || 'Channel'}</p>
                        <p className="text-purple-300/60 text-xs">{channel.platform || 'Platform'}</p>
                      </div>
                      <Badge className={channel.active ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-300'}>
                        {channel.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Monitor className="h-10 w-10 text-purple-400/50 mx-auto mb-3" />
                  <p className="text-purple-200 mb-2">No channels connected yet</p>
                  <p className="text-purple-300/60 text-sm mb-4">Add streaming platforms in your Restream dashboard</p>
                  <Button 
                    variant="outline" 
                    className="border-purple-500/30"
                    onClick={() => window.open('https://restream.io/channel', '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Restream Dashboard
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* How to Go Live */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-400" />
              How to Go Live
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
                <div>
                  <h4 className="text-white font-medium">Connect Restream</h4>
                  <p className="text-purple-300/70 text-sm">Link your free Restream account above</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
                <div>
                  <h4 className="text-white font-medium">Add Platforms in Restream</h4>
                  <p className="text-purple-300/70 text-sm">Connect YouTube, Twitch, Facebook, etc. in your Restream dashboard</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">3</div>
                <div>
                  <h4 className="text-white font-medium">Copy Stream Key to OBS</h4>
                  <p className="text-purple-300/70 text-sm">Use the Server URL and Stream Key above in your streaming software</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">4</div>
                <div>
                  <h4 className="text-white font-medium">Start Streaming!</h4>
                  <p className="text-purple-300/70 text-sm">Hit "Start Streaming" in OBS - you'll be live on all platforms instantly</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LiveStudio;
